import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  PageInfo,
  NormalCreative,
  ShopType,
  LandingPageStrategy,
  DestinationUrlType,
} from '@tt4b/ttam-enums';
import {
  CreatePageParams,
  StateEnum,
  useCreatePageApi,
  useCreativeIs,
  useGetCatalogPagePreviewUrl,
  useGetShopPagePreviewUrl,
  useStdAdgroupIs,
  useStdCampaignIs,
  useStore,
  useUpdatePageApi,
} from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { Ref, computed } from '@vue/composition-api';
import { checkURL } from '@tt4b/ttam-utils';

const { state } = useStore();
const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);

export async function useGeneratePage(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { adgroupData, creativeData } = options;
  const isDLP = creativeData.value.landingPageStrategy === LandingPageStrategy.DLP;
  const isInstantPage = creativeData.value.destinationUrlType === DestinationUrlType.instantPage;
  const isNonDlpInstantPage = !isDLP && isInstantPage;
  const deeplinkUrl = isNonDlpInstantPage ? creativeData.value.openUrlInstantPage : creativeData.value.openUrl;
  const { externalUrl } = creativeData.value;
  let pageId = '';
  const params = {
    pageId: creativeData.value.pageId,
    advertiserId: aadvid.value,
    cardId: creativeData.value.cardId,
    // 补充 `https://` ，后端需要校验是否是合法的URL
    ctaUrl: `${checkURL(externalUrl) ? '' : 'https://'}${externalUrl}`,
    deeplinkUrl: deeplinkUrl || '',
    ctaType: creativeData.value.callToAction,
    renderingShoppingConfiguration: {
      productSetId: creativeData.value.productSetId || '0',
      productOrdering: creativeData.value.dpaProducts,
      productCatalogId: adgroupData.value.productPlatformId,
      bcId: adgroupData.value.catalogAuthorizedBc || '0',
    },
  };
  if (!creativeData.value.pageId) {
    pageId = await useCreatePageApi(params);
  } else {
    pageId = await useUpdatePageApi(params);
  }
  return {
    pageId,
  };
}

/**
 * 这个hooks 适配竞价
 * 调整逻辑: 传入campaignData，用于判断是否是汽车广告 如果是汽车广告 不需要感知cardId
 */
export function usePropsErrorTipVisible(options: {
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData>;
}) {
  const { campaignData, creativeData } = options;
  const { isLeadAdsAutoInventoryAds } = useStdCampaignIs({ campaignFormData: campaignData });

  const pageId = computed(() => creativeData.value.pageId);
  const visible = computed(() => {
    if (isLeadAdsAutoInventoryAds.value) {
      return !pageId.value && creativeData.value.pageInfo?.createFail && !creativeData.value.pageInfo?.loading;
    }

    return (
      !pageId.value &&
      creativeData.value.pageInfo?.createFail &&
      !creativeData.value.pageInfo?.loading &&
      creativeData.value.cardId
    );
  });
  return {
    visible,
  };
}

export async function useGetPreviewUrl(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
}): Promise<PageInfo> {
  const { campaignData, adgroupData, creativeData } = options;
  const { isProductFromCatalog, isProductFromShop, isProductFromShowcase } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  try {
    if (isProductFromShop.value || isProductFromShowcase.value) {
      const params = { productOrdering: [] };
      if (isProductFromShop.value) {
        Object.assign(params, {
          shopId: adgroupData.value.shopId,
          shopType: adgroupData.value.shopType,
          productCatalogId: adgroupData.value.productPlatformId,
          productOrdering: creativeData.value.spuList,
        });
      }
      if (isProductFromShowcase.value) {
        Object.assign(params, {
          identityId: adgroupData.value.identityId,
          identityType: adgroupData.value.identityType,
          shopType: ShopType.ecommShop,
          productOrdering: creativeData.value.showcaseSpuList.map(item => item.spuId),
        });
      }
      if (params.productOrdering.length > 0) {
        let url = await useGetShopPagePreviewUrl(params);
        return {
          shopPreviewUrl: url ? (url += `&timestamp=${Date.now()}`) : '',
          createFail: false,
          loading: false,
        };
      }
    } else if (isProductFromCatalog.value && creativeData.value.pageId) {
      let { previewCatalogUrl, previewSingleUrl } = await useGetCatalogPagePreviewUrl({
        advertiserId: aadvid.value,
        pageId: creativeData.value.pageId,
        productId: creativeData.value.catalogVideoInfo?.productId,
      });
      return {
        singlePreviewUrl: previewSingleUrl ? (previewSingleUrl += `&timestamp=${Date.now()}`) : '',
        catalogPreviewUrl: previewCatalogUrl ? (previewCatalogUrl += `&timestamp=${Date.now()}`) : '',
        createFail: false,
        loading: false,
      };
    }
  } catch {
    return {
      shopPreviewUrl: '',
      singlePreviewUrl: '',
      catalogPreviewUrl: '',
      createFail: true,
      loading: false,
    };
  }
}

/**
 * 获取预览地址
 * 调整逻辑: 如果是汽车广告 url取 aiaPreviewUrl
 */
export function usePropsPreviewUrl(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  normalCreative: Ref<NormalCreative>;
  createMode: Ref<string>;
}) {
  const { campaignData, adgroupData, creativeData, normalCreative, createMode } = options;
  const { isLeadAdsAutoInventoryAds } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isStandardAdsWithShop, isStandardAdsWithCatalog, isStandardAdsWithShowcase } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isSingleVideo, isSingleImage } = useCreativeIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    normalCreativeData: normalCreative,
    createMode,
  });
  const previewUrl = computed(() => {
    if (isStandardAdsWithShop.value || isStandardAdsWithShowcase.value) {
      return creativeData.value.pageInfo?.shopPreviewUrl;
    } else if (isStandardAdsWithCatalog.value) {
      return isSingleVideo.value || isSingleImage.value
        ? creativeData.value.pageInfo?.singlePreviewUrl
        : creativeData.value.pageInfo?.catalogPreviewUrl;
    } else if (isLeadAdsAutoInventoryAds.value) {
      return creativeData.value.pageInfo?.aiaPreviewUrl;
    }
    return '';
  });
  return {
    previewUrl,
  };
}

export function usePropsInstantPageLabel(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { campaignData, adgroupData } = options;
  const { isStandardAdsWithShop } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const label = computed(() =>
    isStandardAdsWithShop.value
      ? i18n.t('module_creative_landing_page_label')
      : i18n.t('module_creative_instant_page_label'),
  );

  return {
    label,
  };
}
